import { AppState } from '../domain/state/AppState';
import type { GameStateMachine } from '../domain/state/GameStateMachine';
import type { EventBus } from '../services/EventBus';
import { formatGlobalBest } from '../features/leaderboard/models/global-best';

const formatScore = (score: number): string => Math.max(0, score).toString().padStart(6, '0');

export type UiActions = {
  confirm: () => void;
  togglePause: () => void;
  toggleMute: () => void;
  replayIntro: () => void;
  openLeaderboard: (opener: HTMLButtonElement) => void;
};

export class UiController {
  private readonly score: HTMLElement;
  private readonly globalBestValues: NodeListOf<HTMLElement>;
  private readonly lives: HTMLElement;
  private readonly ammo: HTMLElement;
  private readonly feedback: HTMLElement;
  private readonly ready: HTMLElement;
  private readonly pause: HTMLElement;
  private readonly gameOver: HTMLElement;
  private readonly loading: HTMLElement;
  private readonly muteButtons: NodeListOf<HTMLButtonElement>;
  private readonly disposers: Array<() => void> = [];
  private feedbackTimer?: number;

  public constructor(
    private readonly root: HTMLElement,
    machine: GameStateMachine,
    events: EventBus,
    actions: UiActions,
  ) {
    this.root.innerHTML = `
      <main id="experience" aria-label="Retro Space Fight experience">
        <div id="game-shell" tabindex="0" aria-label="Pixel-art space arcade. Use WASD or arrow keys to move and Space to fire.">
          <div id="game-canvas-container"></div>
          <div id="ui-layer">
            <div id="loading" class="overlay is-visible" role="status">
              <div class="brand">RETRO SPACE FIGHT</div>
              <div class="loading-bar"><span></span></div>
              <p>CALIBRATING STAR MAP...</p>
            </div>
            <div id="hud" aria-live="polite">
              <div>SCORE <span data-score>000000</span></div>
              <div>BEST&nbsp; <span data-best data-global-best-value>------</span></div>
              <div class="lives-line">LIVES <span data-lives aria-label="3 lives">◆ ◆ ◆</span></div>
              <div class="ammo-line">AMMO&nbsp; <span data-ammo aria-label="12 of 20 shots">12 / 20</span></div>
            </div>
            <div id="game-feedback" role="status" aria-live="polite"></div>
            <div id="scroll-hint" class="pixel-hint">SCROLL TO LAUNCH <span>↓</span></div>
            <section id="ready" class="overlay compact" aria-label="Ready">
              <div class="ready-actions">
                <button class="hero-action" data-confirm>PRESS SPACE TO BEGIN</button>
                <button class="ready-leaderboard" type="button" data-open-leaderboard>LEADERBOARD</button>
              </div>
              <p>WASD / ARROWS TO MOVE · SPACE TO FIRE</p>
            </section>
            <section id="pause" class="overlay compact" role="dialog" aria-modal="true" aria-label="Game paused">
              <h2>PAUSED</h2><p>PRESS P OR ESC TO CONTINUE</p>
              <button data-pause>RESUME</button><button data-mute>SOUND</button>
            </section>
            <section id="game-over" class="overlay compact" role="dialog" aria-modal="true" aria-label="Mission failed">
              <div class="leaderboard-card">
                <h2>MISSION FAILED</h2>
                <p class="final-score">YOUR SCORE <span data-final-score>000000</span><br>BEST&nbsp; <span data-final-best data-global-best-value>------</span></p>
                <form id="score-submit-form" class="score-submit-form" novalidate>
                  <label for="leaderboard-nickname">ENTER CALLSIGN</label>
                  <div class="score-submit-row">
                    <input id="leaderboard-nickname" name="nickname" type="text" minlength="2" maxlength="16" autocomplete="nickname" spellcheck="false" inputmode="text">
                    <button type="submit" data-submit-score disabled>SUBMIT SCORE</button>
                  </div>
                </form>
                <p id="score-submit-status" class="score-submit-status" role="status" aria-live="polite"></p>
                <div id="score-saved-summary" class="score-saved-summary" tabindex="-1" hidden>
                  <span>GLOBAL RANK <b data-global-rank>--</b></span>
                  <span>BEST <b data-global-best data-global-best-value>------</b></span>
                </div>
                <div id="leaderboard-board-home">
                  <section id="leaderboard-board" class="leaderboard-board" data-component="shared-leaderboard" aria-labelledby="leaderboard-title">
                    <h3 id="leaderboard-title">TOP PILOTS</h3>
                    <ol id="leaderboard-list" tabindex="0" aria-live="polite" aria-busy="true">
                      <li class="leaderboard-empty">LOADING TOP PILOTS...</li>
                    </ol>
                  </section>
                </div>
                <div class="game-over-actions">
                  <button data-confirm>PLAY AGAIN</button>
                  <button data-replay>REPLAY DEMO</button>
                </div>
              </div>
            </section>
            <dialog id="leaderboard-dialog" class="leaderboard-dialog" aria-modal="true" aria-labelledby="leaderboard-title">
              <div class="leaderboard-dialog__content">
                <button class="leaderboard-dialog__close" type="button" data-close-leaderboard>CLOSE</button>
                <div data-leaderboard-dialog-mount></div>
              </div>
            </dialog>
            <button id="mute" class="icon-button" data-mute aria-label="Toggle sound">SOUND ON</button>
            <button id="pause-button" class="icon-button" data-pause aria-label="Pause game">PAUSE</button>
            <div id="touch-controls" aria-label="Touch controls">
              <div id="touch-pad" aria-label="Drag to move"><span></span></div>
              <button id="fire-button" aria-label="Fire">FIRE</button>
            </div>
            <div id="rotate-prompt">ROTATE DEVICE TO PLAY ↻</div>
          </div>
        </div>
        <div id="scroll-driver" aria-hidden="true"></div>
      </main>`;
    this.score = this.require('[data-score]');
    this.globalBestValues = this.root.querySelectorAll<HTMLElement>('[data-global-best-value]');
    this.lives = this.require('[data-lives]');
    this.ammo = this.require('[data-ammo]');
    this.feedback = this.require('#game-feedback');
    this.ready = this.require('#ready');
    this.pause = this.require('#pause');
    this.gameOver = this.require('#game-over');
    this.loading = this.require('#loading');
    this.muteButtons = this.root.querySelectorAll<HTMLButtonElement>('[data-mute]');
    this.root
      .querySelectorAll<HTMLElement>('[data-confirm]')
      .forEach((element) => element.addEventListener('click', actions.confirm));
    this.root
      .querySelectorAll<HTMLElement>('[data-pause]')
      .forEach((element) => element.addEventListener('click', actions.togglePause));
    this.root
      .querySelectorAll<HTMLElement>('[data-mute]')
      .forEach((element) => element.addEventListener('click', actions.toggleMute));
    this.require('[data-replay]').addEventListener('click', actions.replayIntro);
    const leaderboardButton = this.root.querySelector<HTMLButtonElement>('[data-open-leaderboard]');
    if (leaderboardButton) {
      leaderboardButton.addEventListener('click', () => actions.openLeaderboard(leaderboardButton));
    }
    this.disposers.push(
      machine.subscribe((_from, to) => this.renderState(to)),
      events.on('game:score-changed', ({ score }) => {
        this.score.textContent = formatScore(score);
      }),
      events.on('leaderboard:global-best-changed', ({ value }) => {
        const formatted = formatGlobalBest(value);
        this.globalBestValues.forEach((element) => (element.textContent = formatted));
      }),
      events.on('player:lives-changed', ({ lives, maxLives }) => {
        this.lives.textContent = Array.from({ length: maxLives }, (_, index) =>
          index < lives ? '◆' : '◇',
        ).join(' ');
        this.lives.setAttribute('aria-label', `${lives} lives remaining`);
      }),
      events.on('player:ammo-changed', ({ ammo, maxAmmo }) => {
        this.ammo.textContent =
          ammo === 0 ? 'EMPTY' : `${ammo.toString().padStart(2, '0')} / ${maxAmmo}`;
        this.ammo.setAttribute('aria-label', `${ammo} of ${maxAmmo} shots remaining`);
        this.ammo.parentElement?.classList.toggle('is-low', ammo <= 3);
        this.ammo.parentElement?.classList.toggle('is-empty', ammo === 0);
      }),
      events.on('player:ammo-empty', () => {
        this.ammo.parentElement?.classList.remove('is-empty-bump');
        requestAnimationFrame(() => this.ammo.parentElement?.classList.add('is-empty-bump'));
      }),
      events.on('ui:feedback', ({ message, tone }) => {
        if (this.feedbackTimer !== undefined) clearTimeout(this.feedbackTimer);
        this.feedback.textContent = message;
        this.feedback.dataset.tone = tone;
        this.feedback.classList.add('is-visible');
        this.feedbackTimer = window.setTimeout(() => {
          this.feedback.classList.remove('is-visible');
          this.feedbackTimer = undefined;
        }, 1_100);
      }),
      events.on('game:over', ({ score }) => {
        this.require('[data-final-score]').textContent = formatScore(score);
      }),
      events.on('audio:mute-changed', ({ muted }) => {
        this.muteButtons.forEach(
          (button) => (button.textContent = muted ? 'SOUND OFF' : 'SOUND ON'),
        );
      }),
      events.on('intro:progress', ({ progress }) => {
        this.require('#scroll-hint').style.opacity = String(Math.max(0, 1 - progress * 8));
      }),
    );
  }

  public finishLoading(): void {
    this.loading.classList.add('is-complete');
    window.setTimeout(() => this.loading.classList.remove('is-visible'), 420);
  }

  public setMobileCopy(mobile: boolean): void {
    const button = this.ready.querySelector<HTMLButtonElement>('[data-confirm]');
    if (button) button.textContent = mobile ? 'TAP TO BEGIN' : 'PRESS SPACE TO BEGIN';
  }

  public destroy(): void {
    if (this.feedbackTimer !== undefined) clearTimeout(this.feedbackTimer);
    this.disposers.forEach((dispose) => dispose());
    this.root.replaceChildren();
  }

  private renderState(state: AppState): void {
    this.root.dataset.state = state;
    this.ready.classList.toggle('is-visible', state === AppState.Ready);
    this.pause.classList.toggle('is-visible', state === AppState.Paused);
    this.gameOver.classList.toggle('is-visible', state === AppState.GameOver);
    if (state === AppState.Paused) this.pause.querySelector<HTMLButtonElement>('button')?.focus();
    if (state === AppState.GameOver)
      this.gameOver.querySelector<HTMLInputElement>('#leaderboard-nickname')?.focus();
  }

  private require(selector: string): HTMLElement {
    const element = this.root.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing UI element: ${selector}`);
    return element;
  }
}
